import { useEffect, useRef, useState } from "preact/hooks";
import { UUID } from "./sugar_uuid.ts";
import { useToast } from "../../components/toast/toast.tsx";
import { useDialog } from "../../components/dialog/dialog.tsx";
import { Section } from "../../components/ui/section.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { useTranslator } from "../../hooks/i18n.ts";
import { Icon } from "../../components/icon/icon.tsx";

interface InfoItem {
    uuid: string;
    uuid_label?: string;
    label: string;
    value: string;
    preset?: boolean;
}

interface CommandItem {
    uuid: string;
    label: string;
}

export const BluetoothConfigPage = () => {
    const t = useTranslator();
    const toast = useToast();
    
    const [supportBluetooth, setSupportBluetooth] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [serverId, setServerId] = useState("");
    
    // WiFi Form
    const [ssid, setSsid] = useState("");
    const [password, setPassword] = useState("");
    const [key, setKey] = useState("FarPov");
    const [wifiLock, setWifiLock] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    
    // Scan State
    const [isScanning, setIsScanning] = useState(false);
    const [scannedWifis, setScannedWifis] = useState<string[]>([]);
    
    // Dialog
    const wifiDialog = useDialog({
        title: t({ zh: "选择 WiFi", en: "Select WiFi" }),
        showCloseButton: true,
        children: (
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {scannedWifis.length === 0 ? (
                    <p style={{ textAlign: "center", color: "#888" }}>{t({ zh: "未找到 WiFi", en: "No WiFi found" })}</p>
                ) : (
                    <div class="grid" style={{ gridTemplateColumns: "1fr", gap: "0.5rem" }}>
                        {scannedWifis.map(wifi => (
                            <button 
                                key={wifi} 
                                class="outline" 
                                style={{ textAlign: "left", justifyContent: "flex-start", padding: "0.5rem 1rem" }}
                                onClick={() => {
                                    setSsid(wifi);
                                    wifiDialog.close();
                                }}
                            >
                                {wifi}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )
    });

    // Data
    const [characteristicsList, setCharacteristicsList] = useState<any[]>([]);
    const [infoList, setInfoList] = useState<InfoItem[]>([]);
    const [commandList, setCommandList] = useState<CommandItem[]>([]);
    const [commandOutput, setCommandOutput] = useState("");
    const [commandOutputShouldRefresh, setCommandOutputShouldRefresh] = useState(false);
    
    // Counters
    const [customInfoCount, setCustomInfoCount] = useState(0);
    const [customCommandCount, setCustomCommandCount] = useState(0);

    const isIphone = navigator.userAgent.indexOf('iPhone') > -1 || navigator.userAgent.indexOf('iphone') > -1;

    useEffect(() => {
        if ((navigator as any).bluetooth) {
            setSupportBluetooth(true);
        }
    }, []);

    // Watch isConnected to initialize data
    useEffect(() => {
        if (isConnected) {
            initializeData();
        }
    }, [isConnected]);

    const initializeData = async () => {
        console.log('Connected!');
        const newInfoList: InfoItem[] = [];
        
        newInfoList.push({
            preset: true,
            uuid: '',
            label: t({ zh: "设备 ID", en: "Device ID" }),
            value: serverId
        });
        newInfoList.push({
            preset: true,
            uuid: UUID.DEVICE_MODEL,
            label: t({ zh: "型号", en: "Model" }),
            value: ' '
        });
        newInfoList.push({
            preset: true,
            uuid: UUID.WIFI_NAME,
            label: t({ zh: "WiFi", en: "WiFi" }),
            value: '...'
        });
        newInfoList.push({
            preset: true,
            uuid: UUID.IP_ADDRESS,
            label: 'IP Address',
            value: '...'
        });

        setInfoList(newInfoList);

        try {
            await subscribeCharacteristic(UUID.WIFI_NAME);
            await subscribeCharacteristic(UUID.IP_ADDRESS);
            await readInfoCharacteristic(UUID.DEVICE_MODEL);
            await subscribeCharacteristic(UUID.NOTIFY_MESSAGE);
            await subscribeCharacteristic(UUID.CUSTOM_COMMAND_NOTIFY);

            // Handle custom info
            const customInfos = characteristicsList.filter((i: any) => i.uuid.indexOf(UUID.CUSTOM_INFO_LABEL) >= 0).map((item: any) => ({
                uuid: item.uuid.replace(UUID.CUSTOM_INFO_LABEL, UUID.CUSTOM_INFO),
                uuid_label: item.uuid,
                label: '-',
                value: '',
                preset: false
            }));

            // Update info list with custom infos
            setInfoList(prev => [...prev, ...customInfos]);

            // We need to read/subscribe to the newly added custom infos
            // Since setInfoList is async, we use the local variable `customInfos` combined with `newInfoList` logic
            // But wait, the previous `newInfoList` items are already processed? 
            // Actually, let's just process the custom ones now.
            
            for (const info of customInfos) {
                await readInfoCharacteristic(info.uuid_label);
                await subscribeCharacteristic(info.uuid);
            }

            // Handle custom commands
            const customCommands = characteristicsList.filter((i: any) => i.uuid.indexOf(UUID.CUSTOM_COMMAND_LABEL) >= 0).map((item: any) => ({
                uuid: item.uuid,
                label: '...'
            }));
            setCommandList(customCommands);

            for (const cmd of customCommands) {
                await readCommandLabel(cmd.uuid);
            }

        } catch (e) {
            console.error("Error initializing data", e);
            toast("Error initializing data: " + e, "error");
        }
    };

    const ab2str = (buf: ArrayBuffer) => {
        return String.fromCharCode.apply(null, new Uint8Array(buf) as any);
    };

    const mapDeviceModelToProduct = (raw: string) => {
        const text = (raw ?? "").replace(/\u0000/g, "").trim();
        if (!text) {
            return text;
        }
        const lower = text.toLowerCase();

        if (lower.includes("zero 2 w") || lower.includes("zero 2w")) {
            return "Farpov Pro";
        }
        if (lower.includes("compute module 4") || lower.includes("cm4")) {
            return "Farpov 5.5K";
        }
        if (lower.includes("4 model b") || lower.includes("raspberry pi 4") || lower.includes("model b")) {
            return "Farpov 4K";
        }

        if (lower.includes("raspberry pi")) {
            return "Farpov";
        }
        return text;
    };

    const str2abs = (str: string) => {
        let val = '';
        for (let i = 0; i < str.length; i++) {
            if (val === '') {
                val = str.charCodeAt(i).toString(16);
            } else {
                val += ',' + str.charCodeAt(i).toString(16);
            }
        }
        let valArray = val.split(',');
        let bufferArray: ArrayBuffer[] = [];
        while (valArray.length > 0) {
            let value = valArray.splice(0, 20).join(',');
            // @ts-ignore
            bufferArray.push(new Uint8Array(value.match(/[\da-f]{2}/gi).map(function (h) {
                return parseInt(h, 16);
            })).buffer);
        }
        return bufferArray;
    };

    const getCharacteristic = (uuid: string) => {
        return characteristicsList.find((i: any) => i.uuid === uuid);
    };

    const subscribeCharacteristic = (uuid: string) => {
        return new Promise<void>(async resolve => {
            const char = getCharacteristic(uuid);
            if (!char) {
                console.warn(`Characteristic ${uuid} not found`);
                resolve();
                return;
            }

            char.addEventListener('characteristicvaluechanged', (event: any) => {
                const valueBuf = event.target.value.buffer;
                const valueStr = ab2str(valueBuf);

                if (event.target.uuid === UUID.NOTIFY_MESSAGE) {
                    toast(valueStr, "error");
                } else if (event.target.uuid === UUID.CUSTOM_COMMAND_NOTIFY) {
                    setCommandOutput(prev => {
                        let output = prev;
                        // Since we can't access state immediately inside event listener without refs or functional updates,
                        // we use functional update.
                        // However, logic says: if shouldRefresh, clear first.
                        // This is tricky with functional updates.
                        // Let's rely on a ref for 'shouldRefresh' if needed, but here's a simplified version:
                        
                        // We need to implement the logic:
                        // if (this.commandOutputShouldRefresh) { ... }
                        
                        // Let's use a mutable ref for output builder to simplify
                        return output; 
                    });
                    
                    // Re-implementing correctly:
                    handleCommandNotify(valueStr);

                } else {
                    // Update info list
                    setInfoList(prevList => prevList.map(item => {
                        if (item.uuid === uuid) {
                            const value = uuid === UUID.DEVICE_MODEL ? mapDeviceModelToProduct(valueStr) : valueStr;
                            return { ...item, value };
                        }
                        if (item.uuid_label === uuid) {
                            return { ...item, label: valueStr };
                        }
                        return item;
                    }));
                }
            });
            await char.startNotifications();
            resolve();
        });
    };
    
    // Ref for command output state management
    const commandOutputRef = useRef({ output: "", shouldRefresh: false });
    
    const handleCommandNotify = (msg: string) => {
        let { output, shouldRefresh } = commandOutputRef.current;
        
        if (shouldRefresh) {
            shouldRefresh = false;
            output = '';
        }
        output = output + msg;
        if (output.endsWith('&#&')) {
            output = output.replace('&#&', '');
            shouldRefresh = true;
        }
        
        commandOutputRef.current = { output, shouldRefresh };
        setCommandOutput(output);
        setCommandOutputShouldRefresh(shouldRefresh);
    };

    const readInfoCharacteristic = (uuid: string) => {
        return new Promise<void>(resolve => {
            const char = getCharacteristic(uuid);
            if (!char) {
                resolve();
                return;
            }
            char.readValue()
                .then((i: any) => i.buffer)
                .then(ab2str)
                .then((value: string) => {
                    setInfoList(prevList => prevList.map(item => {
                        if (item.uuid === uuid) {
                            const mapped = uuid === UUID.DEVICE_MODEL ? mapDeviceModelToProduct(value) : value;
                            return { ...item, value: mapped };
                        }
                        if (item.uuid_label === uuid) {
                            return { ...item, label: value };
                        }
                        return item;
                    }));
                    resolve();
                });
        });
    };

    const readCommandLabel = (uuid: string) => {
        return new Promise<void>(resolve => {
            const char = getCharacteristic(uuid);
            if (!char) {
                resolve();
                return;
            }
            char.readValue()
                .then((i: any) => i.buffer)
                .then(ab2str)
                .then((label: string) => {
                    setCommandList(prev => prev.map(item => {
                        if (item.uuid === uuid) {
                            return { ...item, label };
                        }
                        return item;
                    }));
                    resolve();
                });
        });
    };

    const connectDevice = () => {
        const nav = navigator as any;
        
        const options: any = {
            optionalServices: [UUID.SERVICE_ID]
        };

        if (debugMode) {
            options.acceptAllDevices = true;
        } else {
            options.filters = [
                { services: [UUID.SERVICE_ID], namePrefix: "farpov" },
                { services: [UUID.SERVICE_ID], namePrefix: "Farpov" },
                { services: [UUID.SERVICE_ID], namePrefix: "FarPov" },
                { services: [UUID.SERVICE_ID], namePrefix: "FARPOV" },
            ];
        }

        nav.bluetooth.requestDevice(options)
        .then((device: any) => {
            toast("Connecting...", "normal");
            return device.gatt.connect();
        })
        .then((server: any) => {
            setServerId(server.device.id);
            return server.getPrimaryService(UUID.SERVICE_ID);
        })
        .then((service: any) => {
            if (isIphone) {
                return webBleConnect(service);
            } else {
                return service.getCharacteristics();
            }
        })
        .then((characteristics: any) => {
            setCharacteristicsList(characteristics);
            setIsConnected(true);
            toast("Connected!", "success");
        })
        .catch((e: any) => {
            console.error(e);
            toast("Connection failed: " + e.message, "error");
        });
    };

    const webBleConnect = (service: any) => {
        return new Promise(async resolve => {
             // Implementation of iOS specific logic
             // ... (Logic from App.vue)
             
             let cInfoCount = 0;
             let cCmdCount = 0;

             try {
                const charInfoCount = await service.getCharacteristic(UUID.PREFIX + UUID.CUSTOM_INFO_COUNT);
                const valInfo = await charInfoCount.readValue();
                cInfoCount = parseInt(ab2str(valInfo.buffer));
                setCustomInfoCount(cInfoCount);

                const charCmdCount = await service.getCharacteristic(UUID.PREFIX + UUID.CUSTOM_COMMAND_COUNT);
                const valCmd = await charCmdCount.readValue();
                cCmdCount = parseInt(ab2str(valCmd.buffer));
                setCustomCommandCount(cCmdCount);
             } catch (e) {
                 console.log("Error reading counts", e);
             }

             let customInfoList = [];
             for (let index = 0; index < cInfoCount; index++) {
                let ending = (index + 1).toString(16);
                ending = '0'.repeat(4 - ending.length) + ending;
                customInfoList.push(UUID.PREFIX + UUID.CUSTOM_INFO + ending);
                customInfoList.push(UUID.PREFIX + UUID.CUSTOM_INFO_LABEL + ending);
             }
             
             let customCommandList = [];
             for (let index = 0; index < cCmdCount; index++) {
                let ending = (index + 1).toString(16);
                ending = '0'.repeat(4 - ending.length) + ending;
                customCommandList.push(UUID.PREFIX + UUID.CUSTOM_COMMAND_LABEL + ending);
             }

             const chars = await Promise.all([
                service.getCharacteristic(UUID.SERVICE_NAME),
                service.getCharacteristic(UUID.DEVICE_MODEL),
                service.getCharacteristic(UUID.WIFI_NAME),
                service.getCharacteristic(UUID.IP_ADDRESS),
                service.getCharacteristic(UUID.NOTIFY_MESSAGE),
                service.getCharacteristic(UUID.INPUT_SEP),
                service.getCharacteristic(UUID.CUSTOM_COMMAND_INPUT),
                service.getCharacteristic(UUID.CUSTOM_COMMAND_NOTIFY),
                ...customInfoList.map(i => service.getCharacteristic(i)),
                ...customCommandList.map(i => service.getCharacteristic(i))
             ]);
             
             resolve(chars);
        });
    };

    const sendSeparately = async (array: ArrayBuffer[], uuid: string) => {
        for (const i in array) {
            const char = getCharacteristic(uuid);
            if (char) {
                await char.writeValue(array[i]);
                await new Promise(r => setTimeout(r, 400));
            }
        }
    };

    const inputWifi = () => {
        let keyVal = key.trim().replace(/\|/g, "*");
        let ssidVal = ssid.trim().replace(/\|/g, "*");
        let passwordVal = password.trim().replace(/\|/g, "*");
        
        if (ssidVal === '') {
            toast(t({ zh: "WiFi 名称（SSID）不能为空。", en: "SSID cannot be empty." }), "warn");
            return;
        }
        if (passwordVal === '') {
            toast(t({ zh: "WiFi 密码不能为空。", en: "Password cannot be empty." }), "warn");
            return;
        }
        
        setWifiLock(true);
        setTimeout(() => {
            setWifiLock(false);
        }, 4000);
        
        let sendArray = str2abs(`${keyVal}%&%${ssidVal}%&%${passwordVal}&#&`);
        sendSeparately(sendArray, UUID.INPUT_SEP);
        toast(t({ zh: "已发送 WiFi 配置", en: "Sent WiFi credentials" }), "success");
    };

    const sendCommand = (uuid: string) => {
        let sendArray = str2abs(`${key}%&%${uuid.slice(-4)}&#&`);
        sendSeparately(sendArray, UUID.CUSTOM_COMMAND_INPUT);
    };

    const previewNewUrl = `http://farpov.local:8000/index.html`;
    const previewLegacyUrl = `http://raspberrypi.local:8000/index.html`;

    return (
        <Section>
            <PageHeader title={t({zh: "安装调试", en: "Install & Debug"})} />
            
            <div class="container">
                <article class="card">
                    <header>
                        {t({ zh: "快速预览", en: "Quick Preview" })}
                        <small style={{ marginLeft: "0.35rem", color: "var(--pico-muted-color)", fontWeight: 500 }}>
                            {t({
                                zh: "（需要相机上打开预览，连接相机热点）",
                                en: "(Enable Preview on camera, connect to camera hotspot)",
                            })}
                        </small>
                    </header>
                    
                    <div style={{ display: "flex", flexWrap: "wrap", margin: "-0.5rem" }}>
                        <a
                            href={previewNewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="button"
                            class="button"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textDecoration: "none",
                                height: "auto",
                                marginBottom: 0,
                                margin: "0.5rem",
                                flex: "1 1 260px",
                            }}
                        >
                            <Icon name="external-link" size={18} style={{ marginRight: "0.5rem" }} />
                            {t({ zh: "打开预览窗口", en: "Open Preview Window" })}
                        </a>
                        <a
                            href={previewLegacyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="button"
                            class="button outline"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textDecoration: "none",
                                height: "auto",
                                marginBottom: 0,
                                margin: "0.5rem",
                                flex: "1 1 260px",
                            }}
                        >
                            {t({ zh: "旧设备入口", en: "Legacy Entry" })}
                        </a>
                    </div>
                </article>

                {!supportBluetooth && (
                    <article class="card warn">
                        <header>{t({zh: "未检测到蓝牙能力", en: "Web Bluetooth Not Found"})}</header>
                        <p>
                            {t({
                                zh: "您的浏览器暂不支持网页蓝牙功能。请确保您的设备和浏览器支持 Web-Bluetooth API。（已在 macOS 和 Android 的 Chrome 浏览器以及 iOS 的 WebBLE 浏览器上测试通过）",
                                en: "Web Bluetooth is not supported. Please ensure your device and browser support the Web-Bluetooth API. (Tested on Chrome for macOS/Android and WebBLE for iOS)"
                            })}
                        </p>
                        <p style={{ fontSize: "0.9em", color: "#666" }}>
                            <a href="https://apps.apple.com/us/app/webble/id1193531073" target="_blank" style={{ marginRight: "1rem" }}>
                                {t({zh: "下载 WebBLE (iOS)", en: "Get WebBLE (iOS)"})}
                            </a>
                            <a href="https://github.com/WebBluetoothCG/web-bluetooth/blob/master/implementation-status.md" target="_blank">
                                {t({zh: "查看兼容性列表", en: "Check Compatibility"})}
                            </a>
                        </p>
                    </article>
                )}

                {supportBluetooth && !isConnected && (
                    <article class="card">
                        <header>{t({ zh: "连接到 Farpov", en: "Connect to Farpov" })}</header>
                        <div style={{ marginBottom: "1rem" }}>
                            <label>
                                <input 
                                    type="checkbox" 
                                    checked={debugMode} 
                                    onChange={(e: any) => setDebugMode(e.target.checked)} 
                                /> 
                                {t({ zh: "显示所有设备（调试模式）", en: "Show All Devices (Debug Mode)" })}
                            </label>
                            <small style={{ color: "#888", display: "block", marginTop: "0.5rem" }}>
                                {debugMode 
                                    ? t({ zh: "将扫描所有蓝牙设备。如找不到设备可开启此项。", en: "Will scan for all Bluetooth devices. Use this if your device is not showing up." })
                                    : t({ zh: "正在扫描 Farpov 设备…", en: "Scanning for Farpov devices..." })}
                            </small>
                        </div>
                        <button onClick={connectDevice}>{t({ zh: "搜索设备", en: "Discover Device" })}</button>
                    </article>
                )}

                {isConnected && (
                    <div class="grid">
                        <article class="card">
                            <header>{t({ zh: "设备信息", en: "Device Information" })}</header>
                            <table class="striped">
                                <tbody>
                                    {infoList.map(item => (
                                        <tr key={item.uuid || item.label}>
                                            <th>{item.label}</th>
                                            <td>{item.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </article>

                        <article class="card">
                            <header>{t({ zh: "WiFi 设置", en: "WiFi Setting" })}</header>
                            <label>
                                {t({ zh: "WiFi 名称（SSID）", en: "WiFi Name (SSID)" })}
                                <input type="text" value={ssid} onInput={(e: any) => setSsid(e.target.value)} placeholder={t({ zh: "WiFi 名称", en: "WiFi Name" })} />
                            </label>
                            <label>
                                {t({ zh: "密码", en: "Password" })}
                                <input type="password" value={password} onInput={(e: any) => setPassword(e.target.value)} placeholder={t({ zh: "密码", en: "Password" })} />
                            </label>
                            <label>
                                {t({ zh: "密钥（默认：FarPov）", en: "Key (Default: FarPov)" })}
                                <input type="text" value={key} onInput={(e: any) => setKey(e.target.value)} placeholder={t({ zh: "密钥", en: "Key" })} />
                            </label>
                            <button onClick={inputWifi} disabled={wifiLock}>{t({ zh: "提交 WiFi 配置", en: "Submit WiFi Config" })}</button>
                        </article>
                    </div>
                )}

                {isConnected && (
                    <article class="card">
                        <header>{t({ zh: "自定义命令", en: "Custom Commands" })}</header>
                        {commandList.length > 0 ? (
                            <div>
                                <div class="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "20px" }}>
                                    {commandList.map(item => (
                                        <button key={item.uuid} onClick={() => sendCommand(item.uuid)} class="outline">
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                                <label>
                                    {t({ zh: "密钥", en: "Key" })}
                                    <input type="text" value={key} onInput={(e: any) => setKey(e.target.value)} />
                                </label>
                                <label>
                                    {t({ zh: "输出", en: "Output" })}
                                    <textarea value={commandOutput} rows={10} readOnly />
                                </label>
                            </div>
                        ) : (
                            <p>No custom commands found.</p>
                        )}
                    </article>
                )}
            </div>
        </Section>
    );
};
